import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { 
  Settings, 
  Palette, 
  Globe, 
  CreditCard, 
  Truck, 
  Mail, 
  Shield, 
  Bell,
  Save,
  RotateCcw,
  Zap,
  Eye,
  Monitor,
  Smartphone,
  Tablet,
  Upload,
  Download,
  Image as ImageIcon,
  Clock
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/hooks/useLanguage';
import { useAuth } from '@/hooks/useAuth';
import { PaymentFeesConfig } from '@/components/admin/PaymentFeesConfig';
import { CronJobsManager } from '@/components/admin/CronJobsManager';
import { DatabaseBackupManager } from '@/components/admin/DatabaseBackupManager';
import { SupabaseMigrationManager } from '@/components/admin/SupabaseMigrationManager';
import { Fail2BanManager } from '@/components/admin/Fail2BanManager';
import { MFASettingsManager } from '@/components/admin/MFASettingsManager';
import { MFAAdminManager } from '@/components/admin/MFAAdminManager';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { TranslationMigrator } from '@/components/admin/TranslationMigrator';
import { MissingTranslationsAdder } from '@/components/admin/MissingTranslationsAdder';
import { AutoTranslator } from '@/components/admin/AutoTranslator';
import TranslationChecker from '@/components/admin/TranslationChecker';
import { BulkProductTranslation } from '@/components/admin/BulkProductTranslation';

interface SiteSetting {
  id: string;
  key: string;
  value: any;
  description: string;
  category: string;
  data_type: string;
}

interface ConfigCategory {
  id: string;
  name: string;
  icon: any;
  description: string;
  settings: ConfigSection[];
}

interface ConfigSection {
  title: string;
  settings: ConfigItem[];
}

interface ConfigItem {
  key: string;
  label: string;
  description: string;
  type: 'text' | 'number' | 'boolean' | 'color' | 'slider' | 'select' | 'textarea' | 'image' | 'logo';
  value: any;
  options?: { label: string; value: string }[];
  min?: number;
  max?: number;
  step?: number;
}

type PreviewDevice = 'desktop' | 'tablet' | 'mobile';

export function ConfigurationManager() {
  const [siteSettings, setSiteSettings] = useState<SiteSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeCategory, setActiveCategory] = useState('general');
  const [hasChanges, setHasChanges] = useState(false);
  const [localValues, setLocalValues] = useState<Record<string, any>>({});
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>('desktop');
  const [previewKey, setPreviewKey] = useState(0);
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const { toast } = useToast();
  const { t } = useLanguage();
  const { profile } = useAuth();
  
  const isSuperAdmin = profile?.system_role === 'super_admin';
  const isAdminOrSuperAdmin = profile?.system_role === 'super_admin' || profile?.system_role === 'admin';

  // Get current value from database or default
  const getSettingValue = (key: string, defaultValue: any) => {
    const setting = siteSettings.find(s => s.key === key);
    if (setting) {
      try {
        return JSON.parse(setting.value);
      } catch {
        return setting.value;
      }
    }
    return defaultValue;
  };

  // Build lightweight HTML for inline preview (no JS, no app reload)
  const previewHtml = useMemo(() => {
    const siteName = getSettingValue('site_name', 'Nazandoo Marketplace');
    const tagline = getSettingValue('site_tagline', 'Votre marketplace locale');
    const heroImage = getSettingValue('hero_image_url', '');
    const logo = getSettingValue('logo_url', '');
    const copyright = getSettingValue('footer_copyright', '© 2024 Nazandoo. Tous droits réservés.');
    const address = getSettingValue('footer_address', 'Kinshasa, RD Congo');
    const facebook = getSettingValue('footer_facebook', '');
    const twitter = getSettingValue('footer_twitter', '');
    const instagram = getSettingValue('footer_instagram', '');
    const primaryColor = getSettingValue('primary_color', '#3B82F6');
    const borderRadius = getSettingValue('border_radius', 8);

    const bg = (typeof heroImage === 'string' && (heroImage.startsWith('http') || heroImage.startsWith('/')))
      ? `background-image: url('${heroImage}'); background-size: cover; background-position: center;`
      : `background: linear-gradient(135deg, ${primaryColor}15, ${primaryColor}30);`;

    const socialLinks = [
      { url: facebook, icon: 'Facebook', label: 'Facebook' },
      { url: twitter, icon: 'X', label: 'Twitter' },
      { url: instagram, icon: 'Instagram', label: 'Instagram' }
    ].filter(s => s.url && s.url.startsWith('http'));

    return `<!doctype html>
<html lang="fr">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<style>
  :root { 
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Noto Sans, Helvetica Neue, Arial, 'Apple Color Emoji', 'Segoe UI Emoji';
    --primary: ${primaryColor};
    --radius: ${borderRadius}px;
  }
  * { box-sizing: border-box; }
  body { margin:0; color:#111827; background:#ffffff; line-height:1.6; }
  .header { 
    padding:16px 20px; 
    border-bottom:1px solid #e5e7eb; 
    font-weight:600; 
    display:flex;
    align-items:center;
    gap:12px;
    background:#ffffff;
  }
  .logo { 
    height:32px; 
    width:auto;
    object-fit:contain;
  }
  .hero { 
    ${bg} 
    padding:60px 20px; 
    min-height:280px; 
    display:flex; 
    align-items:center; 
    justify-content:center; 
    text-align:center;
    position:relative;
  }
  .hero::before {
    content:'';
    position:absolute;
    inset:0;
    background:rgba(255,255,255,0.9);
  }
  .hero-content {
    position:relative;
    z-index:1;
  }
  .hero h1 { 
    margin:0 0 12px; 
    font-size:32px; 
    font-weight:700;
    color:var(--primary);
  }
  .hero p { 
    margin:0; 
    color:#374151; 
    font-size:16px; 
  }
  .footer { 
    padding:24px 20px; 
    border-top:1px solid #e5e7eb; 
    color:#6b7280; 
    font-size:13px;
    background:#f9fafb;
  }
  .footer-content {
    max-width:800px;
    margin:0 auto;
    text-align:center;
  }
  .footer-address {
    margin-bottom:12px;
    font-weight:500;
    color:#374151;
  }
  .footer-social {
    display:flex;
    justify-content:center;
    gap:16px;
    margin-bottom:12px;
    flex-wrap:wrap;
  }
  .footer-social a {
    color:var(--primary);
    text-decoration:none;
    font-size:12px;
    padding:4px 12px;
    border:1px solid #e5e7eb;
    border-radius:var(--radius);
    transition:all 0.2s;
  }
  .footer-social a:hover {
    background:var(--primary);
    color:#ffffff;
    border-color:var(--primary);
  }
  .footer-copyright {
    color:#9ca3af;
  }
  .btn {
    display:inline-block;
    padding:10px 24px;
    background:var(--primary);
    color:#ffffff;
    text-decoration:none;
    border-radius:var(--radius);
    font-weight:600;
    margin-top:16px;
    transition:opacity 0.2s;
  }
  .btn:hover {
    opacity:0.9;
  }
</style>
</head>
<body>
  <div class="header">
    ${logo && (logo.startsWith('http') || logo.startsWith('/')) 
      ? `<img src="${logo}" alt="${siteName}" class="logo" />` 
      : `<svg width="32" height="32" viewBox="0 0 32 32" fill="none"><rect width="32" height="32" rx="6" fill="${primaryColor}"/><path d="M16 8L24 20H8L16 8Z" fill="white"/></svg>`
    }
    <span>${siteName}</span>
  </div>
  <section class="hero">
    <div class="hero-content">
      <h1>${siteName}</h1>
      <p>${tagline}</p>
      <a href="/" target="_blank" rel="noopener noreferrer" class="btn">Explorer</a>
    </div>
  </section>
  <div class="footer">
    <div class="footer-content">
      ${address ? `<div class="footer-address">${address}</div>` : ''}
      ${socialLinks.length > 0 ? `
        <div class="footer-social">
          ${socialLinks.map(s => `<a href="${s.url}" target="_blank">${s.label}</a>`).join('')}
        </div>
      ` : ''}
      <div class="footer-copyright">${copyright}</div>
    </div>
  </div>
</body>
</html>`;
  }, [siteSettings]);

  // Make configCategories reactive to siteSettings changes
  const configCategories = useMemo(() => [
    {
      id: 'general',
      name: t('admin.config.categories.general'),
      icon: Settings,
      description: t('admin.config.categories.general_desc'),
      settings: [
        {
          title: t('admin.config.site_info'),
          settings: [
            {
              key: 'site_name',
              label: t('admin.config.site_name'),
              description: t('admin.config.platform_name'),
              type: 'text',
              value: getSettingValue('site_name', 'Nazandoo Marketplace')
            },
            {
              key: 'site_tagline',
              label: t('admin.config.tagline'),
              description: t('admin.config.tagline_desc'),
              type: 'text',
              value: getSettingValue('site_tagline', 'Votre marketplace locale')
            },
            {
              key: 'site_description',
              label: t('admin.config.description'),
              description: t('admin.config.description_desc'),
              type: 'textarea',
              value: getSettingValue('site_description', 'Découvrez les meilleurs produits locaux et internationaux directement à Kinshasa')
            }
          ]
        },
        {
          title: t('admin.config.logo_images'),
          settings: [
            {
              key: 'logo_url',
              label: t('admin.config.logo_url'),
              description: t('admin.config.logo_url_desc'),
              type: 'logo',
              value: getSettingValue('logo_url', '')
            },
            {
              key: 'hero_image_url',
              label: t('admin.config.hero_image'),
              description: t('admin.config.hero_image_desc'),
              type: 'image',
              value: getSettingValue('hero_image_url', '')
            }
          ]
        },
        {
          title: t('admin.config.contact_support'),
          settings: [
            {
              key: 'contact_email',
              label: t('admin.config.contact_email'),
              description: t('admin.config.contact_email_desc'),
              type: 'text',
              value: getSettingValue('contact_email', 'contact@nazandoo.cd')
            },
            {
              key: 'support_phone',
              label: t('admin.config.support_phone'),
              description: t('admin.config.support_phone_desc'),
              type: 'text',
              value: getSettingValue('support_phone', '+243 123 456 789')
            },
            {
              key: 'maintenance_mode',
              label: t('admin.config.maintenance_mode'),
              description: t('admin.config.maintenance_mode_desc'),
              type: 'boolean',
              value: getSettingValue('maintenance_mode', false)
            },
            {
              key: 'show_announcement_banner',
              label: t('admin.config.show_announcement_banner'),
              description: t('admin.config.show_announcement_banner_desc'),
              type: 'boolean',
              value: getSettingValue('show_announcement_banner', true)
            },
            {
              key: 'password_reset_expiry_minutes',
              label: t('admin.config.password_reset_expiry'),
              description: t('admin.config.password_reset_expiry_desc'),
              type: 'number',
              value: getSettingValue('password_reset_expiry_minutes', 30),
              min: 5,
              max: 1440,
              step: 5
            }
          ]
        },
        {
          title: t('admin.config.footer'),
          settings: [
            {
              key: 'footer_address',
              label: t('admin.config.footer_address'),
              description: t('admin.config.footer_address_desc'),
              type: 'text',
              value: getSettingValue('footer_address', 'Kinshasa, RD Congo')
            },
            {
              key: 'footer_copyright',
              label: t('admin.config.footer_copyright'),
              description: t('admin.config.footer_copyright_desc'),
              type: 'text',
              value: getSettingValue('footer_copyright', '© 2024 Nazandoo. Tous droits réservés.')
            }
          ]
        },
        {
          title: 'Statistiques',
          settings: [
            {
              key: 'satisfied_customers_count',
              label: 'Clients satisfaits',
              description: 'Nombre de clients satisfaits affichés sur la page d\'accueil. Si défini à 0, le système calculera automatiquement depuis les avis clients (rating ≥ 4 étoiles). Sinon, utilisera la valeur manuelle.',
              type: 'number',
              value: getSettingValue('satisfied_customers_count', 0),
              min: 0,
              max: 999999,
              step: 1
            }
          ]
        }
      ]
    },
    {
      id: 'appearance',
      name: t('admin.config.appearance'),
      icon: Palette,
      description: t('admin.config.appearance_desc'),
      settings: [
        {
          title: t('admin.config.theme_colors'),
          settings: [
            {
              key: 'primary_color',
              label: t('admin.config.primary_color'),
              description: t('admin.config.primary_color_desc'),
              type: 'color',
              value: '#3B82F6'
            },
            {
              key: 'secondary_color',
              label: t('admin.config.secondary_color'),
              description: t('admin.config.secondary_color_desc'),
              type: 'color',
              value: '#10B981'
            },
            {
              key: 'dark_mode_enabled',
              label: t('admin.config.dark_mode'),
              description: t('admin.config.dark_mode_desc'),
              type: 'boolean',
              value: true
            }
          ]
        },
        {
          title: t('admin.config.ui'),
          settings: [
            {
              key: 'border_radius',
              label: t('admin.config.border_radius'),
              description: t('admin.config.border_radius_desc'),
              type: 'slider',
              value: 8,
              min: 0,
              max: 20,
              step: 1
            },
            {
              key: 'animation_speed',
              label: t('admin.config.animation_speed'),
              description: t('admin.config.animation_speed_desc'),
              type: 'slider',
              value: 300,
              min: 100,
              max: 800,
              step: 50
            }
          ]
        },
        {
          title: 'Bannière publicitaire',
          settings: [
            {
              key: 'banner_autoplay_enabled',
              label: 'Rotation automatique',
              description: 'Activer la rotation automatique des bannières publicitaires',
              type: 'boolean' as const,
              value: getSettingValue('banner_autoplay_enabled', true)
            },
            {
              key: 'banner_autoplay_delay',
              label: 'Délai de rotation (ms)',
              description: 'Durée d\'affichage de chaque bannière avant de passer à la suivante',
              type: 'slider' as const,
              value: getSettingValue('banner_autoplay_delay', 5000),
              min: 2000,
              max: 10000,
              step: 500
            }
          ]
        }
      ]
    },
    {
      id: 'payment',
      name: t('admin.config.payment'),
      icon: CreditCard,
      description: t('admin.config.payment_desc'),
      settings: [
        {
          title: t('admin.config.payment_methods'),
          settings: [
            {
              key: 'stripe_enabled',
              label: t('admin.config.stripe_enabled'),
              description: t('admin.config.stripe_enabled_desc'),
              type: 'boolean',
              value: getSettingValue('stripe_enabled', true)
            },
            {
              key: 'mobile_money_enabled',
              label: t('admin.config.mobile_money'),
              description: t('admin.config.mobile_money_desc'),
              type: 'boolean',
              value: getSettingValue('mobile_money_enabled', true)
            },
            {
              key: 'cash_on_delivery',
              label: t('admin.config.cash_on_delivery'),
              description: t('admin.config.cash_on_delivery_desc'),
              type: 'boolean',
              value: getSettingValue('cash_on_delivery', true)
            }
          ]
        },
        {
          title: t('admin.config.fees'),
          settings: [
            {
              key: 'transaction_fee',
              label: t('admin.config.transaction_fee'),
              description: t('admin.config.transaction_fee_desc'),
              type: 'slider',
              value: 2.5,
              min: 0,
              max: 10,
              step: 0.1
            },
            {
              key: 'min_order_amount',
              label: t('admin.config.min_order'),
              description: t('admin.config.min_order_desc'),
              type: 'number',
              value: 5000
            }
          ]
        },
        {
          title: t('admin.config.payment_simulation'),
          settings: [
            {
              key: 'payment_country_simulation_enabled',
              label: t('admin.config.payment_country_simulation_enabled'),
              description: t('admin.config.payment_country_simulation_enabled_desc'),
              type: 'boolean',
              value: getSettingValue('payment_country_simulation_enabled', false)
            },
            {
              key: 'payment_country_simulation_country',
              label: t('admin.config.payment_country_simulation_country'),
              description: t('admin.config.payment_country_simulation_country_desc'),
              type: 'select',
              value: getSettingValue('payment_country_simulation_country', 'CD'),
              options: [
                { label: 'RDC (CD)', value: 'CD' },
                { label: 'France (FR)', value: 'FR' },
                { label: 'United States (US)', value: 'US' },
                { label: 'Canada (CA)', value: 'CA' },
                { label: 'United Kingdom (GB)', value: 'GB' },
                { label: 'Nigeria (NG)', value: 'NG' }
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'shipping',
      name: t('admin.config.shipping'),
      icon: Truck,
      description: t('admin.config.shipping_desc'),
      settings: [
        {
          title: t('admin.config.shipping_options'),
          settings: [
            {
              key: 'default_shipping_cost',
              label: t('admin.config.default_shipping_cost'),
              description: t('admin.config.default_shipping_cost_desc'),
              type: 'number',
              value: 2000
            },
            {
              key: 'free_shipping_threshold',
              label: t('admin.config.free_shipping_threshold'),
              description: t('admin.config.free_shipping_threshold_desc'),
              type: 'number',
              value: 50000
            },
            {
              key: 'estimated_delivery_days',
              label: t('admin.config.delivery_days'),
              description: t('admin.config.delivery_days_desc'),
              type: 'slider',
              value: 3,
              min: 1,
              max: 14,
              step: 1
            }
          ]
        }
      ]
    },
    {
      id: 'notifications',
      name: t('admin.config.notifications'),
      icon: Bell,
      description: t('admin.config.notifications_desc'),
      settings: [
        {
          title: t('admin.config.email_notifications'),
          settings: [
            {
              key: 'email_notifications_enabled',
              label: t('admin.config.email_enabled'),
              description: t('admin.config.email_enabled_desc'),
              type: 'boolean',
              value: true
            },
            {
              key: 'order_confirmation_email',
              label: t('admin.config.order_confirmation'),
              description: t('admin.config.order_confirmation_desc'),
              type: 'boolean',
              value: true
            },
            {
              key: 'shipping_notification_email',
              label: t('admin.config.shipping_notification'),
              description: t('admin.config.shipping_notification_desc'),
              type: 'boolean',
              value: true
            }
          ]
        },
        {
          title: t('admin.config.sms_notifications'),
          settings: [
            {
              key: 'sms_notifications_enabled',
              label: t('admin.config.sms_enabled'),
              description: t('admin.config.sms_enabled_desc'),
              type: 'boolean',
              value: false
            }
          ]
        }
      ]
    },
    {
      id: 'security',
      name: t('admin.config.security'),
      icon: Shield,
      description: t('admin.config.security_desc'),
      settings: [
        {
          title: t('admin.config.authentication'),
          settings: [
            {
              key: 'require_email_verification',
              label: t('admin.config.email_verification'),
              description: t('admin.config.email_verification_desc'),
              type: 'boolean',
              value: true
            },
            {
              key: 'password_min_length',
              label: t('admin.config.password_length'),
              description: t('admin.config.password_length_desc'),
              type: 'slider',
              value: 8,
              min: 6,
              max: 20,
              step: 1
            },
            {
              key: 'session_timeout',
              label: t('admin.config.session_timeout'),
              description: t('admin.config.session_timeout_desc'),
              type: 'slider',
              value: 120,
              min: 15,
              max: 480,
              step: 15
            }
          ]
        }
      ]
    },
    {
      id: 'translations',
      name: t('admin.config.translations'),
      icon: Globe,
      description: t('admin.config.translations_desc'),
      settings: [
        {
          title: t('admin.config.language_settings'),
          settings: [
            {
              key: 'default_language',
              label: t('admin.config.default_language'),
              description: t('admin.config.default_language_desc'),
              type: 'select',
              value: getSettingValue('default_language', 'fr'),
              options: [
                { label: 'Français', value: 'fr' },
                { label: 'English', value: 'en' },
                { label: 'Lingala', value: 'lg-fr' },
                { label: 'Kiswahili', value: 'sw' }
              ]
            },
            {
              key: 'enable_auto_translation',
              label: t('admin.config.auto_translation'),
              description: t('admin.config.auto_translation_desc'),
              type: 'boolean',
              value: getSettingValue('enable_auto_translation', true)
            },
            {
              key: 'fallback_to_french',
              label: t('admin.config.fallback_french'),
              description: t('admin.config.fallback_french_desc'),
              type: 'boolean',
              value: getSettingValue('fallback_to_french', true)
            }
          ]
        },
        {
          title: t('admin.config.active_languages'),
          settings: [
            {
              key: 'language_french_enabled',
              label: 'Français',
              description: t('admin.config.language_fr_desc'),
              type: 'boolean',
              value: getSettingValue('language_french_enabled', true)
            },
            {
              key: 'language_english_enabled',
              label: 'English',
              description: t('admin.config.language_en_desc'),
              type: 'boolean',
              value: getSettingValue('language_english_enabled', true)
            },
            {
              key: 'language_lingala_enabled',
              label: 'Lingala',
              description: t('admin.config.language_lg_desc'),
              type: 'boolean',
              value: getSettingValue('language_lingala_enabled', true)
            },
            {
              key: 'language_swahili_enabled',
              label: 'Kiswahili',
              description: t('admin.config.language_sw_desc'),
              type: 'boolean',
              value: getSettingValue('language_swahili_enabled', true)
            }
          ]
        }
      ]
    },
    {
      id: 'automation',
      name: 'Automatisation',
      icon: Clock,
      description: 'Gestion des tâches automatisées et cron jobs',
      settings: []
    }
  ], [siteSettings, t]); // Re-calculate when siteSettings or language changes

  // Filtrer les catégories selon le rôle utilisateur
  const filteredCategories = useMemo(() => {
    return configCategories.filter(category => {
      // L'onglet automatisation est visible pour les admin et super_admin
      if (category.id === 'automation') {
        return isAdminOrSuperAdmin;
      }
      return true;
    });
  }, [configCategories, isSuperAdmin]);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*')
        .order('category', { ascending: true });

      if (error) throw error;
      setSiteSettings(data || []);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast({
        title: t('common.error'),
        description: t('admin.config.error_loading'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key: string, value: any) => {
    try {
      // Update in database immediately
      const { error } = await supabase
        .from('site_settings')
        .upsert({
          key,
          value: JSON.stringify(value),
          category: getCurrentSettingCategory(key),
          data_type: getCurrentSettingType(key),
          description: getCurrentSettingDescription(key)
        }, { onConflict: 'key' });

      if (error) throw error;

      // Force a fresh fetch to ensure UI updates
      await fetchSettings();

      toast({
        title: t('admin.config.settings_updated'),
        description: `${key} ${t('common.saved')}`,
      });
    } catch (error) {
      console.error('Error updating setting:', error);
      toast({
        title: t('common.error'),
        description: t('admin.config.error_saving'),
        variant: "destructive",
      });
    }
  };

  const getCurrentSettingCategory = (key: string): string => {
    for (const category of configCategories) {
      for (const section of category.settings) {
        for (const setting of section.settings) {
          if (setting.key === key) return category.id;
        }
      }
    }
    return 'general';
  };

  const getCurrentSettingType = (key: string): string => {
    for (const category of configCategories) {
      for (const section of category.settings) {
        for (const setting of section.settings) {
          if (setting.key === key) return setting.type;
        }
      }
    }
    return 'text';
  };

  const getCurrentSettingDescription = (key: string): string => {
    for (const category of configCategories) {
      for (const section of category.settings) {
        for (const setting of section.settings) {
          if (setting.key === key) return setting.description;
        }
      }
    }
    return '';
  };

  const saveAllSettings = async () => {
    setSaving(true);
    try {
      const allSettings: any[] = [];
      
      configCategories.forEach(category => {
        category.settings.forEach(section => {
          section.settings.forEach(setting => {
            // Use locally-edited value if present, otherwise use DB value
            const effectiveValue = localValues[setting.key] !== undefined
              ? localValues[setting.key]
              : setting.value;
            allSettings.push({
              key: setting.key,
              value: JSON.stringify(effectiveValue),
              description: setting.description,
              category: category.id,
              data_type: setting.type
            });
          });
        });
      });

      // Upsert all settings
      for (const setting of allSettings) {
        const { error } = await supabase
          .from('site_settings')
          .upsert(setting, { onConflict: 'key' });

        if (error) throw error;
      }

      toast({
        title: t('admin.config.changes_saved'),
        description: t('admin.config.changes_saved_desc'),
      });
      
      setHasChanges(false);
      setLocalValues({});
      await fetchSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: t('common.error'),
        description: t('admin.config.error_saving'),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    // Reset logic here
    setHasChanges(false);
    toast({
      title: t('admin.config.reset_confirm'),
      description: t('admin.config.reset_confirm_desc'),
    });
  };

  const handleLogoDownload = () => {
    const logoUrl = getSettingValue('logo_url', '');
    if (!logoUrl) {
      toast({
        title: t('admin.config.no_logo'),
        description: t('admin.config.no_logo_desc'),
        variant: "destructive",
      });
      return;
    }

    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = logoUrl;
    link.download = 'logo-nazandoo.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: t('admin.config.download_started'),
      description: t('admin.config.download_started_desc'),
    });
  };

  const renderSettingControl = (setting: ConfigItem) => {
    switch (setting.type) {
      case 'logo':
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Input
                type="text"
                value={localValues[setting.key] ?? setting.value}
                onChange={(e) => {
                  setLocalValues(prev => ({ ...prev, [setting.key]: e.target.value }));
                  setHasChanges(true);
                }}
                placeholder={t('admin.config.logo_url_placeholder')}
                className="flex-1"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogoDownload}
                disabled={!(localValues[setting.key] ?? setting.value)}
              >
                <Download className="h-4 w-4 mr-2" />
                {t('admin.config.download')}
              </Button>
            </div>
            {(localValues[setting.key] ?? setting.value) && (
              <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30">
                <div className="w-12 h-12 bg-background rounded flex items-center justify-center border">
                  {((localValues[setting.key] ?? setting.value) as string).startsWith('http') || ((localValues[setting.key] ?? setting.value) as string).startsWith('/') ? (
                    <img src={localValues[setting.key] ?? setting.value} alt="Logo" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <ImageIcon className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 text-sm">
                  <p className="font-medium">{t('admin.config.logo_preview')}</p>
                  <p className="text-xs text-muted-foreground">{t('admin.config.logo_preview_desc')}</p>
                </div>
              </div>
            )}
          </div>
        );

      case 'image':
        return (
          <div className="space-y-3">
            <Input
              type="text"
              value={localValues[setting.key] ?? setting.value}
              onChange={(e) => {
                setLocalValues(prev => ({ ...prev, [setting.key]: e.target.value }));
                setHasChanges(true);
              }}
              placeholder={t('admin.config.url_placeholder')}
            />
            {(localValues[setting.key] ?? setting.value) && (
              <div className="relative w-full aspect-video rounded-lg overflow-hidden border bg-muted">
                {((localValues[setting.key] ?? setting.value) as string).startsWith('http') || ((localValues[setting.key] ?? setting.value) as string).startsWith('/') ? (
                  <img src={localValues[setting.key] ?? setting.value} alt={t('admin.config.image_preview')} className="w-full h-full object-cover" />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <ImageIcon className="h-12 w-12 text-muted-foreground" />
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 'boolean':
        return (
          <div className="flex items-center space-x-2">
            <Switch
              checked={setting.value}
              onCheckedChange={(checked) => {
                updateSetting(setting.key, checked);
                setHasChanges(true);
              }}
            />
            <span className="text-sm text-muted-foreground">
              {setting.value ? t('admin.config.enabled') : t('admin.config.disabled')}
            </span>
          </div>
        );

      case 'slider':
        return (
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm">{setting.min}</span>
              <Badge variant="outline">{localValues[setting.key] ?? setting.value}</Badge>
              <span className="text-sm">{setting.max}</span>
            </div>
            <Slider
              value={[localValues[setting.key] ?? setting.value]}
              onValueChange={(values) => {
                setLocalValues(prev => ({ ...prev, [setting.key]: values[0] }));
                setHasChanges(true);
              }}
              min={setting.min || 0}
              max={setting.max || 100}
              step={setting.step || 1}
              className="w-full"
            />
          </div>
        );

      case 'color':
        return (
          <div className="flex items-center space-x-2">
            <Input
              type="color"
              value={localValues[setting.key] ?? setting.value}
              onChange={(e) => {
                setLocalValues(prev => ({ ...prev, [setting.key]: e.target.value }));
                setHasChanges(true);
              }}
              className="w-16 h-10 p-1 border rounded cursor-pointer"
            />
            <Input
              type="text"
              value={localValues[setting.key] ?? setting.value}
              onChange={(e) => {
                setLocalValues(prev => ({ ...prev, [setting.key]: e.target.value }));
                setHasChanges(true);
              }}
              className="font-mono text-sm"
              placeholder="#000000"
            />
          </div>
        );

      case 'textarea':
        return (
          <textarea
            value={localValues[setting.key] ?? setting.value}
            onChange={(e) => {
              setLocalValues(prev => ({ ...prev, [setting.key]: e.target.value }));
              setHasChanges(true);
            }}
            className="w-full min-h-[80px] px-3 py-2 border border-input bg-background text-sm rounded-md resize-vertical"
            placeholder={setting.description}
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            value={localValues[setting.key] ?? setting.value}
            onChange={(e) => {
              setLocalValues(prev => ({ ...prev, [setting.key]: parseFloat(e.target.value) || 0 }));
              setHasChanges(true);
            }}
            min={setting.min}
            max={setting.max}
            step={setting.step}
          />
        );

      case 'select':
        return (
          <select
            value={setting.value}
            onChange={(e) => updateSetting(setting.key, e.target.value)}
            className="w-full px-3 py-2 border border-input bg-background text-sm rounded-md"
          >
            {setting.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      default:
        return (
          <Input
            type="text"
            value={localValues[setting.key] ?? setting.value}
            onChange={(e) => {
              setLocalValues(prev => ({ ...prev, [setting.key]: e.target.value }));
              setHasChanges(true);
            }}
            placeholder={setting.description}
          />
        );
    }
  };

  const activeConfig = configCategories.find(c => c.id === activeCategory);

  if (loading) {
    return <div className="flex justify-center items-center h-64">{t('admin.config.loading')}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header avec actions globales */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="h-6 w-6" />
            {t('admin.config.advanced_title')}
          </h2>
          <p className="text-muted-foreground">
            {t('admin.config.advanced_desc')}
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          {hasChanges && (
            <Badge variant="secondary" className="animate-pulse">
              {t('admin.config.unsaved_changes')}
            </Badge>
          )}
          <Button
            variant="outline"
            onClick={resetToDefaults}
            disabled={saving}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            {t('admin.config.reset')}
          </Button>
          <Button
            onClick={saveAllSettings}
            disabled={!hasChanges || saving}
          >
            {saving ? (
              <>
                <Zap className="h-4 w-4 mr-2 animate-spin" />
                {t('admin.config.saving')}
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {t('admin.config.save_all')}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Interface principale */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar des catégories */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{t('admin.config.categories')}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <nav className="space-y-1">
                {filteredCategories.map((category) => {
                  const Icon = category.icon;
                  return (
                    <button
                      key={category.id}
                      onClick={() => setActiveCategory(category.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 text-left text-sm transition-colors hover:bg-muted ${
                        activeCategory === category.id
                          ? 'bg-primary/10 text-primary border-r-2 border-primary'
                          : 'text-muted-foreground'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <div>
                        <div className="font-medium">{category.name}</div>
                        <div className="text-xs text-muted-foreground hidden sm:block">
                          {category.description}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </nav>
            </CardContent>
          </Card>
        </div>

        {/* Contenu principal */}
        <div className="lg:col-span-3">
          {/* Payment Fees Configuration - Special Section */}
          {activeCategory === 'payment' && (
            <div className="mb-6">
              <PaymentFeesConfig />
            </div>
          )}
          
          {/* Automation/Cron Jobs - Special Section (Admin and Super Admin) */}
          {activeCategory === 'automation' && isAdminOrSuperAdmin && (
            <div className="space-y-6">
              <Fail2BanManager />
              <DatabaseBackupManager />
              <SupabaseMigrationManager />
              <CronJobsManager />
            </div>
          )}

          {/* MFA Settings - Section Sécurité (accordéons) */}
          {activeCategory === 'security' && isAdminOrSuperAdmin && (
            <Accordion type="multiple" defaultValue={['auth-basic', 'mfa-policy']} className="space-y-3">

              {/* Authentification de base (paramètres génériques) */}
              {activeConfig && activeConfig.settings.length > 0 && (
                <AccordionItem value="auth-basic" className="border rounded-xl overflow-hidden shadow-sm">
                  <AccordionTrigger className="px-5 py-4 hover:no-underline bg-card hover:bg-muted/30 [&[data-state=open]]:border-b">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Shield className="h-4 w-4 text-primary" />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-sm">Authentification de base</p>
                        <p className="text-xs text-muted-foreground font-normal">Vérification email, longueur mot de passe, durée de session</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-0 pb-0">
                    <div className="p-5 space-y-6">
                      {activeConfig.settings.map((section, sectionIndex) => (
                        <div key={sectionIndex} className="space-y-4">
                          {section.settings.map((setting, settingIndex) => (
                            <div key={setting.key} className="space-y-3">
                              <div className="flex items-start justify-between">
                                <div className="space-y-1 flex-1">
                                  <Label className="text-sm font-medium">{setting.label}</Label>
                                  <p className="text-xs text-muted-foreground">{setting.description}</p>
                                </div>
                              </div>
                              <div className="flex-1">{renderSettingControl(setting)}</div>
                              {settingIndex < section.settings.length - 1 && <Separator />}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}

              {/* Politique MFA */}
              <AccordionItem value="mfa-policy" className="border rounded-xl overflow-hidden shadow-sm">
                <AccordionTrigger className="px-5 py-4 hover:no-underline bg-card hover:bg-muted/30 [&[data-state=open]]:border-b">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-50">
                      <Shield className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-sm">Double authentification (MFA)</p>
                      <p className="text-xs text-muted-foreground font-normal">Politique par groupe, appareils de confiance</p>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-0 pb-0">
                  <div className="p-5">
                    <MFASettingsManager />
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Gestion MFA par utilisateur */}
              <AccordionItem value="mfa-admin" className="border rounded-xl overflow-hidden shadow-sm">
                <AccordionTrigger className="px-5 py-4 hover:no-underline bg-card hover:bg-muted/30 [&[data-state=open]]:border-b">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-orange-50">
                      <Shield className="h-4 w-4 text-orange-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-sm">Gestion MFA par utilisateur</p>
                      <p className="text-xs text-muted-foreground font-normal">Révoquer facteurs, appareils mémorisés, forcer re-enrollment</p>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-0 pb-0">
                  <div className="p-5">
                    <MFAAdminManager />
                  </div>
                </AccordionContent>
              </AccordionItem>

            </Accordion>
          )}
          
          {/* Translations Management - Special Section */}
          {activeCategory === 'translations' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    Traduction des produits
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <BulkProductTranslation />
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    Outils de traduction système
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <MissingTranslationsAdder />
                  <Separator />
                  <TranslationMigrator />
                  <Separator />
                  <TranslationChecker />
                  <Separator />
                  <AutoTranslator />
                </CardContent>
              </Card>
            </div>
          )}
          
          {activeConfig && activeConfig.settings.length > 0 && activeCategory !== 'security' && (
            <div className="space-y-6">
              {activeConfig.settings.map((section, sectionIndex) => (
                <Card key={sectionIndex}>
                  <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                      {(() => {
                        const Icon = activeConfig.icon;
                        return <Icon className="h-5 w-5" />;
                      })()}
                      {section.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {section.settings.map((setting, settingIndex) => (
                      <div key={setting.key} className="space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1 flex-1">
                            <Label className="text-sm font-medium">
                              {setting.label}
                            </Label>
                            <p className="text-xs text-muted-foreground">
                              {setting.description}
                            </p>
                          </div>
                        </div>
                        <div className="flex-1">
                          {renderSettingControl(setting)}
                        </div>
                        {settingIndex < section.settings.length - 1 && <Separator />}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Prévisualisation rapide */}
      <Card className="overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10">
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-primary" />
            {t('admin.config.preview_title')}
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            {t('admin.config.preview_desc')}
          </p>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* Device Selection */}
            <div className="flex items-center justify-center gap-2">
              <Button 
                variant={previewDevice === 'desktop' ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setPreviewDevice('desktop'); setPreviewLoaded(false); }}
                className="flex items-center gap-2 min-w-[100px]"
              >
                <Monitor className="h-4 w-4" />
                <span className="text-xs font-medium">{t('admin.config.device_desktop')}</span>
              </Button>
              <Button 
                variant={previewDevice === 'tablet' ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setPreviewDevice('tablet'); setPreviewLoaded(false); }}
                className="flex items-center gap-2 min-w-[100px]"
              >
                <Tablet className="h-4 w-4" />
                <span className="text-xs font-medium">{t('admin.config.device_tablet')}</span>
              </Button>
              <Button 
                variant={previewDevice === 'mobile' ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setPreviewDevice('mobile'); setPreviewLoaded(false); }}
                className="flex items-center gap-2 min-w-[100px]"
              
              >
                <Smartphone className="h-4 w-4" />
                <span className="text-xs font-medium">{t('admin.config.device_mobile')}</span>
              </Button>
            </div>

            {/* Preview Frame */}
            <div className="relative flex justify-center items-center min-h-[500px] bg-gradient-to-br from-muted/30 to-muted/50 rounded-lg p-6 overflow-hidden">

              {/* Desktop – browser chrome */}
              {previewDevice === 'desktop' && (
                <div className="w-full max-w-3xl bg-zinc-800 rounded-xl shadow-2xl overflow-hidden">
                  <div className="bg-zinc-700 px-4 py-2 flex items-center gap-2">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-400" />
                      <div className="w-3 h-3 rounded-full bg-yellow-400" />
                      <div className="w-3 h-3 rounded-full bg-green-400" />
                    </div>
                    <div className="flex-1 bg-zinc-600 rounded-full h-5 px-3 flex items-center">
                      <span className="text-zinc-300 text-xs truncate">nazandoo.com</span>
                    </div>
                  </div>
                  <div className="h-80 flex flex-col items-center justify-center gap-4 text-zinc-400 bg-zinc-900">
                    <Monitor className="h-14 w-14 opacity-30" />
                    <p className="text-sm font-medium">Simulation Desktop — 1280 × 800</p>
                    <Button
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => window.open('/', '_blank', 'width=1280,height=800,toolbar=no,menubar=no,scrollbars=yes,resizable=yes')}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Ouvrir la simulation
                    </Button>
                  </div>
                </div>
              )}

              {/* Tablet – iPad-style frame */}
              {previewDevice === 'tablet' && (
                <div className="bg-zinc-800 rounded-[28px] shadow-2xl" style={{ padding: '14px', width: '306px' }}>
                  <div className="flex justify-center mb-2">
                    <div className="w-2 h-2 rounded-full bg-zinc-600" />
                  </div>
                  <div className="bg-zinc-900 rounded-lg overflow-hidden flex flex-col items-center justify-center gap-4 text-zinc-400" style={{ height: '402px' }}>
                    <Tablet className="h-12 w-12 opacity-30" />
                    <p className="text-xs font-medium text-center px-4">Simulation Tablette<br/>768 × 1024</p>
                    <Button
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                      onClick={() => window.open('/', '_blank', 'width=768,height=1024,toolbar=no,menubar=no,scrollbars=yes,resizable=yes')}
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      Ouvrir
                    </Button>
                  </div>
                  <div className="flex justify-center mt-2">
                    <div className="w-14 h-1 bg-zinc-600 rounded-full" />
                  </div>
                </div>
              )}

              {/* Mobile – iPhone-style frame */}
              {previewDevice === 'mobile' && (
                <div className="bg-zinc-800 rounded-[40px] shadow-2xl" style={{ padding: '12px 10px', width: '216px' }}>
                  <div className="flex justify-center mb-2">
                    <div className="bg-black rounded-full" style={{ width: '80px', height: '18px' }} />
                  </div>
                  <div className="bg-zinc-900 rounded-[20px] overflow-hidden flex flex-col items-center justify-center gap-4 text-zinc-400" style={{ height: '432px' }}>
                    <Smartphone className="h-10 w-10 opacity-30" />
                    <p className="text-xs font-medium text-center px-4">Simulation Mobile<br/>390 × 844</p>
                    <Button
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                      onClick={() => window.open('/', '_blank', 'width=390,height=844,toolbar=no,menubar=no,scrollbars=yes,resizable=yes')}
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      Ouvrir
                    </Button>
                  </div>
                  <div className="flex justify-center mt-2">
                    <div className="w-16 h-1 bg-zinc-600 rounded-full" />
                  </div>
                </div>
              )}
            </div>

            {/* Preview Info & Actions */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 border-t">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Badge variant="secondary" className="text-xs">
                  {previewDevice === 'desktop' && '1280x800'}
                  {previewDevice === 'tablet' && '768x1024'}
                  {previewDevice === 'mobile' && '390x844'}
                </Badge>
                <span>{t('admin.config.simulation')} {previewDevice}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => window.open('/', '_blank')}>
                  <Eye className="h-4 w-4 mr-1" />
                  {t('admin.config.view_real_site')}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}